import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, IconButton, Avatar, Grid, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Fab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { toast } from 'react-hot-toast';
import config, { getApiUrl, getEndpoint } from '../config';

const API_URL = getEndpoint('SETTINGS');
const MAX_LOGO_SIZE = config.FILE_LIMITS.LOGO;

const Settings = () => {
  const [settings, setSettings] = useState({ logo: '', menu: [], mainButton: { text: '', link: '' } });
  const [logoFile, setLogoFile] = useState(null);
  const [menuDialog, setMenuDialog] = useState(false);
  const [editMenuIndex, setEditMenuIndex] = useState(null);
  const [menuForm, setMenuForm] = useState({ title: '', link: '', submenu: [] });
  const [submenuDialog, setSubmenuDialog] = useState(false);
  const [editSubIndex, setEditSubIndex] = useState(null);
  const [submenuForm, setSubmenuForm] = useState({ title: '', link: '' });
  const [mainButton, setMainButton] = useState({ text: '', link: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('توکن احراز هویت یافت نشد. لطفاً دوباره وارد شوید.');
        window.location.href = '/login';
        return;
      }
      
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setSettings(res.data);
        setMainButton(res.data.mainButton || { text: '', link: '' });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      if (err.response && err.response.status === 401) {
        toast.error('توکن منقضی شده است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else if (err.response && err.response.status === 403) {
        toast.error('توکن نامعتبر است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error('خطا در دریافت تنظیمات');
      }
    }
  };

  const handleLogo = (file) => {
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE) {
      toast.error('حجم لوگو نباید بیشتر از ۵۰ کیلوبایت باشد');
      return;
    }
    setLogoFile(file);
    setSettings(prev => ({ ...prev, logo: URL.createObjectURL(file) }));
  };

  // Menu CRUD
  const handleMenuSave = () => {
    if (!menuForm.title.trim() || !menuForm.link.trim()) {
      toast.error('عنوان و لینک منو الزامی است');
      return;
    }
    let menu = [...settings.menu];
    if (editMenuIndex !== null) {
      menu[editMenuIndex] = { ...menuForm };
    } else {
      menu.push({ ...menuForm });
    }
    setSettings(prev => ({ ...prev, menu }));
    setMenuDialog(false);
    setMenuForm({ title: '', link: '', submenu: [] });
    setEditMenuIndex(null);
  };
  const handleMenuEdit = (idx) => {
    setEditMenuIndex(idx);
    setMenuForm({ ...settings.menu[idx] });
    setMenuDialog(true);
  };
  const handleMenuDelete = (idx) => {
    let menu = [...settings.menu];
    menu.splice(idx, 1);
    setSettings(prev => ({ ...prev, menu }));
  };

  // Submenu CRUD
  const handleSubSave = () => {
    if (!submenuForm.title.trim() || !submenuForm.link.trim()) {
      toast.error('عنوان و لینک زیرمنو الزامی است');
      return;
    }
    let submenu = menuForm.submenu ? [...menuForm.submenu] : [];
    if (editSubIndex !== null) {
      submenu[editSubIndex] = { ...submenuForm };
    } else {
      submenu.push({ ...submenuForm });
    }
    setMenuForm(prev => ({ ...prev, submenu }));
    setSubmenuDialog(false);
    setSubmenuForm({ title: '', link: '' });
    setEditSubIndex(null);
  };
  const handleSubEdit = (idx) => {
    setEditSubIndex(idx);
    setSubmenuForm({ ...menuForm.submenu[idx] });
    setSubmenuDialog(true);
  };
  const handleSubDelete = (idx) => {
    let submenu = [...(menuForm.submenu || [])];
    submenu.splice(idx, 1);
    setMenuForm(prev => ({ ...prev, submenu }));
  };

  // Main Button
  const handleMainButton = (key, value) => {
    setMainButton(prev => ({ ...prev, [key]: value }));
    setSettings(prev => ({ ...prev, mainButton: { ...prev.mainButton, [key]: value } }));
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    if (logoFile) formData.append('logo', logoFile);
    else formData.append('logo',settings.logo)
    formData.append('menu', JSON.stringify(settings.menu));
    formData.append('mainButton', JSON.stringify(mainButton));
    try {
      const token = localStorage.getItem('token');
      await axios.post(API_URL, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تنظیمات ذخیره شد');
      fetchSettings();
    } catch (err) {
      toast.error('خطا در ذخیره تنظیمات');
    }
  };

  return (
    <Box sx={{ p: 3, fontFamily: 'Vazir' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
          تنظیمات سایت
        </Typography>
      </Box>
      {/* Logo */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>لوگو سایت (حداکثر ۵۰ کیلوبایت)</Typography>
        <Button variant="outlined" component="label" sx={{ fontFamily: 'Vazir', mt: 1 }}>
          انتخاب لوگو
          <input type="file" accept="image/*" hidden onChange={e => handleLogo(e.target.files[0])} />
        </Button>
        {settings.logo && <Avatar src={getApiUrl(`/api/media/download?path=${settings.logo.split('/').pop()}`)} sx={{ width: 80, height: 80, mt: 1 }} />}
      </Box>
      {/* Menu */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>منوها</Typography>
        <TableContainer component={Paper} sx={{ fontFamily: 'Vazir' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عنوان</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>لینک</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>زیرمنوها</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.menu.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>{item.title}</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>{item.link}</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>
                    {item.submenu && item.submenu.length > 0 && (
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {item.submenu.map((sub, sidx) => (
                          <li key={sidx} style={{ fontFamily: 'Vazir' }}>{sub.title} ({sub.link})</li>
                        ))}
                      </ul>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="ویرایش منو" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          color="primary" 
                          onClick={() => handleMenuEdit(idx)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف منو" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          color="error" 
                          onClick={() => handleMenuDelete(idx)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Main Button */}
      <Box sx={{ mb: 3, bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir', mb: 1 }}>دکمه اصلی</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="متن دکمه" fullWidth value={mainButton.text} onChange={e => handleMainButton('text', e.target.value)} sx={{ fontFamily: 'Vazir' }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="لینک دکمه" fullWidth value={mainButton.link} onChange={e => handleMainButton('link', e.target.value)} sx={{ fontFamily: 'Vazir' }} />
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ my: 3 }} />
      <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
        ذخیره تنظیمات
      </Button>
      
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="افزودن منو"
        onClick={() => setMenuDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          fontFamily: 'Vazir',
          zIndex: 9999,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}
      >
        <AddIcon />
      </Fab>
      
      {/* Menu Dialog */}
      <Dialog open={menuDialog} onClose={() => setMenuDialog(false)} dir="rtl">
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>{editMenuIndex !== null ? 'ویرایش منو' : 'افزودن منو'}</DialogTitle>
        <DialogContent>
          <TextField label="عنوان" fullWidth value={menuForm.title} onChange={e => setMenuForm(prev => ({ ...prev, title: e.target.value }))} sx={{ fontFamily: 'Vazir', mb: 2 }} />
          <TextField label="لینک" fullWidth value={menuForm.link} onChange={e => setMenuForm(prev => ({ ...prev, link: e.target.value }))} sx={{ fontFamily: 'Vazir', mb: 2 }} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setSubmenuDialog(true)} sx={{ fontFamily: 'Vazir', mb: 2 }}>
            افزودن زیرمنو
          </Button>
          {menuForm.submenu && menuForm.submenu.length > 0 && (
            <TableContainer component={Paper} sx={{ fontFamily: 'Vazir', mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عنوان</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>لینک</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menuForm.submenu.map((sub, idx) => (
                    <TableRow 
                      key={idx}
                      sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                    >
                      <TableCell sx={{ fontFamily: 'Vazir' }}>{sub.title}</TableCell>
                      <TableCell sx={{ fontFamily: 'Vazir' }}>{sub.link}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="ویرایش زیرمنو" sx={{ fontFamily: 'Vazir' }}>
                            <IconButton 
                              color="primary" 
                              onClick={() => handleSubEdit(idx)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="حذف زیرمنو" sx={{ fontFamily: 'Vazir' }}>
                            <IconButton 
                              color="error" 
                              onClick={() => handleSubDelete(idx)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMenuDialog(false)} sx={{ fontFamily: 'Vazir' }}>انصراف</Button>
          <Button onClick={handleMenuSave} variant="contained" sx={{ fontFamily: 'Vazir' }}>{editMenuIndex !== null ? 'ذخیره' : 'افزودن'}</Button>
        </DialogActions>
      </Dialog>
      {/* Submenu Dialog */}
      <Dialog open={submenuDialog} onClose={() => setSubmenuDialog(false)} dir="rtl">
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>{editSubIndex !== null ? 'ویرایش زیرمنو' : 'افزودن زیرمنو'}</DialogTitle>
        <DialogContent>
          <TextField label="عنوان" fullWidth value={submenuForm.title} onChange={e => setSubmenuForm(prev => ({ ...prev, title: e.target.value }))} sx={{ fontFamily: 'Vazir', mb: 2 }} />
          <TextField label="لینک" fullWidth value={submenuForm.link} onChange={e => setSubmenuForm(prev => ({ ...prev, link: e.target.value }))} sx={{ fontFamily: 'Vazir', mb: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmenuDialog(false)} sx={{ fontFamily: 'Vazir' }}>انصراف</Button>
          <Button onClick={handleSubSave} variant="contained" sx={{ fontFamily: 'Vazir' }}>{editSubIndex !== null ? 'ذخیره' : 'افزودن'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings; 